// src/components/YouTubeEmbed.jsx

import React from 'react';
import PropTypes from 'prop-types';

/**
 * A responsive YouTube video embed component.
 * @param {object} props - The component's properties.
 * @param {string} props.url - The full YouTube URL.
 * @returns {JSX.Element|null} The rendered component or null if URL is invalid.
 */
const YouTubeEmbed = ({ url }) => {
  /**
   * Extracts the YouTube video ID from a URL.
   * Handles various YouTube URL formats.
   * @param {string} urlString - The YouTube URL.
   * @returns {string|null} The video ID or null if not found.
   */
  const getVideoId = (urlString) => {
    try {
      const urlObject = new URL(urlString);
      const hostname = urlObject.hostname;

      if (hostname.includes('youtube.com')) {
        return urlObject.searchParams.get('v');
      }

      if (hostname.includes('youtu.be')) {
        return urlObject.pathname.slice(1);
      }
      
      return null; // Not a recognized YouTube URL
    } catch (error) {
      console.error("Invalid URL provided to YouTubeEmbed component.", error);
      return null;
    }
  };

  const videoId = getVideoId(url);

  // If no video ID is found, don't render anything.
  if (!videoId) {
    return (
      <div style={{ color: 'red', margin: '20px' }}>
        Invalid YouTube URL provided.
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&controls=0&modestbranding=1`;
  const decodedUrl = embedUrl.replace(/&amp;/g, '&');
  return (
    // Responsive wrapper
    <div className="video-responsive"> 
      <iframe
        src={decodedUrl}
        title="Embedded YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      ></iframe>
    </div>
  );
};

// Define prop types for type-checking and clarity
YouTubeEmbed.propTypes = {
  url: PropTypes.string.isRequired,
};

export default YouTubeEmbed;